import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument from "../../../document/PortalDocument";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import {WebServiceType, WebServiceTypes,} from "@softvision/webpdf-wsclient-9-typescript";
import MimeType from "../../../document/mimetype/MimeType";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import CompressionDialogFields from "../../field/document/CompressionDialogFields";
import DialogDefault from "../../parameter/PortalDialogDefault";
import Parameter from "../../parameter/Parameter";
import {Slider} from "primereact/slider";
import {InputNumber} from "primereact/inputnumber";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	CompressObjects,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxCompressCompress
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import MessageBox from "../../../../shared/components/MessageBox";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";

export interface CompressionDialogState extends PortalDialogState<ToolboxCompressCompress> {
	isArchive: boolean;
	isSigned: boolean;
}

export class CompressionDialogDefinition extends PortalDialog<ToolboxCompressCompress,
	PortalDialogProperties<ToolboxCompressCompress>, CompressionDialogState> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			isArchive: false,
			isSigned: false
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.compress, Functions.getCompressDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.COMPRESS, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let parameter: ToolboxCompressCompress | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let classList: Array<string> = ["compression-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.compression.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{(this.state.isSigned || this.state.isArchive) &&
						<MessageBox {...this.props} type={MessageType.WARNING}>
							<Translate {...this.props} data={"dialog.compression.info"} namespace={"help"}
									   markdown={true}/>
						</MessageBox>
					}
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12"/>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(CompressionDialogFields.STREAM_SIZE)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-stream-size">
								<Translate {...this.props} data="compression.stream_size.label" namespace="dialog"/>
								<Info {...this.props} data="compression.stream_size.info" options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-8 slider-container">
								<span className="min tooltip" data-pr-tooltip={
									Translate.value("compression.stream_size.min", {ns: "dialog"})
								}>{CompressObjects.getObjectStreamSizeMin()}</span>
								<div className="slider-input">
									<InputNumber inputId="control-stream-size" max={1000} step={1} showButtons={true}
												 value={FunctionHelper.applyValue(
													 parameter?.compressObjects?.objectStreamSize,
													 CompressObjects.getObjectStreamSizeDefault()
												 )}
												 min={CompressObjects.getObjectStreamSizeMin()} useGrouping={false}
												 onValueChange={(e) => this.onChange(
													 CompressionDialogFields.STREAM_SIZE, e.value
												 )}/>
									<Slider step={1} max={1000}
											value={FunctionHelper.applyValue(
												parameter?.compressObjects?.objectStreamSize,
												CompressObjects.getObjectStreamSizeDefault()
											)}
											min={CompressObjects.getObjectStreamSizeMin()}
											onChange={(e) => this.onChange(
												CompressionDialogFields.STREAM_SIZE, e.value as number
											)}/>
								</div>
								<span className="max tooltip" data-pr-tooltip={
									Translate.value("compression.stream_size.max", {ns: "dialog"})
								}>1000</span>
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected onChange(field: CompressionDialogFields, value: any): void {
		let parameter: ToolboxCompressCompress = this.state.parameter!.data;

		switch (field) {
			case CompressionDialogFields.STREAM_SIZE:
				parameter.compressObjects!.objectStreamSize = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(portalDocument)) {
			return;
		}

		this.setState({
			isArchive: portalDocument.isArchive(),
			isSigned: portalDocument.hasSignature()
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-compress small"}/>
					<Translate {...this.props} data="compression.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			compress: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.compress", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.COMPRESS;
	}

	protected getDefaultParameter(): Parameter<ToolboxCompressCompress> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const CompressionDialog = React.memo(CompressionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxCompressCompress>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxCompressCompress>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default CompressionDialog;